import { useObservabilityContext } from "@/hooks/use-observability-context";
import { resetSession, sessionQuery } from "@/services/router-session";
import { finishSentryBookingJourney } from "@/services/sentry-context";
import { SESSION_EXPIRED_EVENT } from "@/services/session-events";
import { useStore } from "@/services/store";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { Outlet, useLocation, useNavigate } from "react-router";

function isBookingJourneyLocation(pathname, search) {
  const isBookingJourneyPath = (path) =>
    path.startsWith("/booking/") ||
    /^\/therapeutes\/[^/]+\/[^/]+\/[^/]+\/prendre-rendez-vous\/?$/.test(path);

  if (isBookingJourneyPath(pathname)) return true;
  if (!pathname.startsWith("/auth")) return false;

  const searchParams = new URLSearchParams(search);
  const redirect = searchParams.get("redirect") || "";
  const redirectPathname = redirect.split("?")[0];
  return (
    isBookingJourneyPath(redirectPathname) || searchParams.has("booking_id")
  );
}

function ObservabilityContextHandler() {
  const activeRole = useStore((state) => state.activeRole);
  // Only the active role's session matters for Sentry and PostHog.
  const sessionQueryResult = useQuery({
    ...sessionQuery(activeRole),
    enabled: !!activeRole,
  });
  const user = sessionQueryResult.data?.user || null;
  const routeLocation = useLocation();

  useObservabilityContext({
    activeRole,
    admin: activeRole === "admin" ? user : null,
    customer: activeRole === "customer" ? user : null,
    therapist: activeRole === "therapist" ? user : null,
    sessionContext: sessionQueryResult.data?.session_context || null,
    pathname: routeLocation.pathname,
    search: routeLocation.search,
  });

  useEffect(() => {
    if (isBookingJourneyLocation(routeLocation.pathname, routeLocation.search))
      return;
    finishSentryBookingJourney();
  }, [routeLocation.pathname, routeLocation.search]);

  return null;
}

function SessionExpiredHandler() {
  const activeRole = useStore((state) => state.activeRole);
  const routeLocation = useLocation();
  // A therapist coming back to the tab gets their session checked again (an
  // account refused or deactivated meanwhile is logged out).
  const sessionQueryResult = useQuery({
    ...sessionQuery(activeRole),
    enabled: !!activeRole,
    refetchOnWindowFocus:
      activeRole === "therapist" &&
      routeLocation.pathname.startsWith("/therapist/")
        ? "always"
        : false,
  });
  const userId = sessionQueryResult.data?.user?._id || "";
  const navigate = useNavigate();
  const isRedirectingRef = useRef(false);
  const previousUserIdRef = useRef("");

  useEffect(() => {
    if (!isRedirectingRef.current) return;
    if (
      ["/admin/auth/login", "/auth", "/auth/therapist/login"].includes(
        routeLocation.pathname
      )
    )
      return;
    isRedirectingRef.current = false;
  }, [routeLocation.pathname]);

  useEffect(() => {
    const handleSessionExpired = (event) => {
      const role = event?.detail?.role;
      if (!["admin", "customer", "therapist"].includes(role)) return;
      if (role !== activeRole) return;
      if (!userId) return;
      if (isRedirectingRef.current) return;

      isRedirectingRef.current = true;
      resetSession(role);
      toast.error("Votre session a expiré. Veuillez vous reconnecter.");
      navigate(
        role === "admin"
          ? "/admin/auth/login?expired=1"
          : role === "therapist"
            ? "/auth/therapist/login?expired=1"
            : "/auth?expired=1",
        { replace: true }
      );
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () =>
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [activeRole, navigate, userId]);

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;
    if (activeRole !== "therapist") return;
    if (!previousUserId || userId) return;
    if (isRedirectingRef.current) return;
    resetSession("therapist");
  }, [activeRole, userId]);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

export function RootLayout() {
  return (
    <>
      <ObservabilityContextHandler />
      <SessionExpiredHandler />
      <ScrollToTop />
      <Outlet />
    </>
  );
}
